import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { ClaimStrategyResponse } from "@patent-studio/api-types";
import { claimStrategyKey } from "@/hooks/query-keys";
import { useClaimSetMoments } from "./claim-set-moments";

/** How often the stage is read again while Praxis tags a claim. */
export const TAGGING_POLL_MS = 2000;
/**
 * How long the page waits for a tag. The server keeps no record of a tag
 * being asked for, so a job that chose the category the claim already had
 * looks like one still running; after the window the claim reads as the tag
 * it has.
 */
export const TAGGING_WINDOW_MS = 30000;

/**
 * Watches the claims a write asked Praxis to tag: reads the stage again while
 * any waits, and lets one go once its category changed, the attorney chose one,
 * the claim is gone, or its own window, from when it was last asked, ends.
 */
export function useTaggingWatch(
  taskId: string,
  stage: ClaimStrategyResponse,
): void {
  const { tagging, settleTagging } = useClaimSetMoments();
  const queryClient = useQueryClient();
  const waitingRef = useRef(tagging);
  waitingRef.current = tagging;

  useEffect(() => {
    const done = [...tagging]
      .filter(([claimId, ask]) => {
        const claim = stage.claims.find((c) => c.claim_id === claimId);
        return (
          claim === undefined ||
          claim.form_id !== ask.formId ||
          claim.form_origin !== "placeholder"
        );
      })
      .map(([claimId]) => claimId);
    if (done.length > 0) settleTagging(done);
  }, [stage, tagging, settleTagging]);

  const waiting = tagging.size > 0;
  useEffect(() => {
    if (!waiting) return undefined;
    const timer = setInterval(() => {
      const now = Date.now();
      const ended = [...waitingRef.current]
        .filter(([, ask]) => now - ask.since >= TAGGING_WINDOW_MS)
        .map(([claimId]) => claimId);
      if (ended.length > 0) settleTagging(ended);
      if (ended.length < waitingRef.current.size) {
        void queryClient.invalidateQueries({
          queryKey: claimStrategyKey(taskId),
        });
      }
    }, TAGGING_POLL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [waiting, queryClient, settleTagging, taskId]);
}
